import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  DashboardAdmin,
  DashboardAdminWeek,
  DashboardDraft,
} from '../models/dashboard';
import { shortDate, weekRange } from '../lib/dashboard';
import DashboardDraftModal from './DashboardDraftModal';

export interface DashboardAdminViewProps {
  admin: DashboardAdmin;
  selectedCompanyId?: string;
  onSelectCompany: (companyId: string) => void;
  // Draft review
  draft?: DashboardDraft | null;
  loadingDraft?: boolean;
  onReviewDraft: (weekStart: string) => void;
  onCloseDraft: () => void;
  // Actions
  onPublish: (weekStart: string, acknowledgeWarnings: boolean) => void;
  onUnpublish: (weekStart: string) => void;
  publishingWeek?: string | null;
  unpublishingWeek?: string | null;
}

const statusLabel = (status: DashboardAdminWeek['status']) => {
  switch (status) {
    case 'PUBLISHED':
      return 'Published';
    case 'PENDING':
      return 'Ready to publish';
    case 'OPEN':
      return 'In progress';
    default:
      return status;
  }
};

export const DashboardAdminView = ({
  admin,
  selectedCompanyId,
  onSelectCompany,
  draft,
  loadingDraft,
  onReviewDraft,
  onCloseDraft,
  onPublish,
  onUnpublish,
  publishingWeek,
  unpublishingWeek,
}: DashboardAdminViewProps) => {
  const [unpublishConfirmWeek, setUnpublishConfirmWeek] =
    useState<string | null>(null);

  const activeCompany =
    admin.companies.find((c) => c.id === selectedCompanyId) || admin.company;

  const currentPublishingWeek = admin.weeks.find(
    (w) => w.weekStart === draft?.weekStart
  );

  return (
    <Container>
      <HeaderSection>
        <div>
          <h1>Weekly Dashboard Publishing</h1>
          <p className="subtitle">
            Review live drafts, verify quality checks, and publish weekly
            reports for clients.
          </p>
        </div>

        {admin.companies.length > 1 ? (
          <CompanyPicker>
            <label htmlFor="company-select">Company:</label>
            <select
              id="company-select"
              value={activeCompany?.id || ''}
              onChange={(e) => onSelectCompany(e.target.value)}
            >
              {admin.companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || `Company ${c.id}`}
                </option>
              ))}
            </select>
          </CompanyPicker>
        ) : (
          <CompanyBadge>
            {activeCompany?.name ||
              (activeCompany?.id
                ? `Company ${activeCompany.id}`
                : 'No company selected')}
          </CompanyBadge>
        )}
      </HeaderSection>

      {/* Publication Status Card */}
      <StatusCard>
        <div className="status-item">
          <span className="label">Status:</span>
          <span className="value-highlight">
            {admin.publishedThrough
              ? `Published through week ending ${shortDate(
                  admin.publishedThrough
                )}`
              : 'No weeks published yet'}
          </span>
        </div>
        {admin.publishedBy && (
          <div className="status-item meta">
            <span>
              Last published by <strong>{admin.publishedBy}</strong>
            </span>
            {admin.publishedAt && (
              <span>
                {' '}
                on {new Date(admin.publishedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        )}
      </StatusCard>

      {/* Weeks Table */}
      <TableCard>
        <TableHeaderRow>
          <h2>Recent Weeks (Last 8 Weeks)</h2>
        </TableHeaderRow>

        <WeeksList>
          {admin.weeks.map((week) => {
            const isPublishing = publishingWeek === week.weekStart;
            const isUnpublishing = unpublishingWeek === week.weekStart;

            return (
              <WeekRow key={week.weekStart}>
                <WeekInfo>
                  <span className="range">{weekRange(week)}</span>
                  <StatusPill className={week.status.toLowerCase()}>
                    <span className="dot" />
                    {statusLabel(week.status)}
                  </StatusPill>
                </WeekInfo>

                <WeekActions>
                  <ActionButton
                    type="button"
                    className="review"
                    onClick={() => onReviewDraft(week.weekStart)}
                    disabled={loadingDraft}
                  >
                    {loadingDraft && draft?.weekStart === week.weekStart
                      ? 'Loading…'
                      : 'Review draft'}
                  </ActionButton>

                  {week.canPublish && (
                    <ActionButton
                      type="button"
                      className="publish"
                      onClick={() => onPublish(week.weekStart, false)}
                      disabled={isPublishing}
                    >
                      {isPublishing ? 'Publishing…' : 'Publish'}
                    </ActionButton>
                  )}

                  {week.canRepublish && (
                    <ActionButton
                      type="button"
                      className="republish"
                      onClick={() => onPublish(week.weekStart, false)}
                      disabled={isPublishing}
                    >
                      {isPublishing ? 'Republishing…' : 'Republish'}
                    </ActionButton>
                  )}

                  {week.canUnpublish && (
                    <ActionButton
                      type="button"
                      className="unpublish"
                      onClick={() => setUnpublishConfirmWeek(week.weekStart)}
                      disabled={isUnpublishing}
                    >
                      {isUnpublishing ? 'Unpublishing…' : 'Unpublish'}
                    </ActionButton>
                  )}
                </WeekActions>
              </WeekRow>
            );
          })}
        </WeeksList>
      </TableCard>

      {/* Unpublish Confirmation Dialog */}
      {unpublishConfirmWeek && (
        <ConfirmScrim onClick={() => setUnpublishConfirmWeek(null)}>
          <ConfirmBox onClick={(e) => e.stopPropagation()}>
            <h3>Unpublish Week?</h3>
            <p>
              Are you sure you want to unpublish the week of{' '}
              <strong>{unpublishConfirmWeek}</strong>? Clients will no longer
              see this weekly report in their portal.
            </p>
            <div className="btn-group">
              <ActionButton
                type="button"
                className="review"
                onClick={() => setUnpublishConfirmWeek(null)}
              >
                Cancel
              </ActionButton>
              <ActionButton
                type="button"
                className="unpublish"
                onClick={() => {
                  onUnpublish(unpublishConfirmWeek);
                  setUnpublishConfirmWeek(null);
                }}
              >
                Confirm Unpublish
              </ActionButton>
            </div>
          </ConfirmBox>
        </ConfirmScrim>
      )}

      {/* Live Draft Review Modal */}
      {draft && (
        <DashboardDraftModal
          draft={draft}
          canPublish={
            currentPublishingWeek?.canPublish ||
            currentPublishingWeek?.canRepublish
          }
          publishing={publishingWeek === draft.weekStart}
          onClose={onCloseDraft}
          onPublish={(acknowledgeWarnings) =>
            onPublish(draft.weekStart, acknowledgeWarnings)
          }
        />
      )}
    </Container>
  );
};

export default DashboardAdminView;

const Container = styled.div`
  max-width: 960px;
  margin: 32px auto 64px;
  padding: 0 24px;
  font-family: inherit;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    color: #1d2939;
  }

  .subtitle {
    margin: 6px 0 0;
    font-size: 14px;
    color: #667085;
  }
`;

const CompanyPicker = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #475467;
  }

  select {
    padding: 8px 14px;
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    background: #ffffff;
    font-size: 14px;
    font-weight: 600;
    color: #1d2939;
    cursor: pointer;

    &:focus {
      outline: none;
      border-color: #325882;
      box-shadow: 0 0 0 3px rgba(50, 88, 130, 0.15);
    }
  }
`;

const CompanyBadge = styled.div`
  padding: 6px 14px;
  background: #f2f4f7;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
`;

const StatusCard = styled.div`
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
  flex-wrap: wrap;
  gap: 12px;

  .status-item {
    font-size: 14px;
    color: #344054;

    .label {
      font-weight: 600;
      color: #475467;
      margin-right: 6px;
    }

    .value-highlight {
      font-weight: 700;
      color: #006c50;
    }

    &.meta {
      font-size: 13px;
      color: #667085;
    }
  }
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);
`;

const TableHeaderRow = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #eaecf0;
  background: #fafafa;

  h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1d2939;
  }
`;

const WeeksList = styled.div`
  display: flex;
  flex-direction: column;
`;

const WeekRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #f2f4f7;
  gap: 16px;
  flex-wrap: wrap;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const WeekInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  .range {
    font-size: 14px;
    font-weight: 600;
    color: #1d2939;
    min-width: 190px;
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 16px;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &.published {
    background: #edfcf2;
    color: #027a48;
    .dot {
      background: #12b76a;
    }
  }

  &.pending {
    background: #fef7ec;
    color: #b54708;
    .dot {
      background: #f79009;
    }
  }

  &.open {
    background: #f2f4f7;
    color: #475467;
    .dot {
      background: #98a2b3;
    }
  }
`;

const WeekActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.review {
    background: #ffffff;
    border: 1px solid #d0d5dd;
    color: #344054;
    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #98a2b3;
    }
  }

  &.publish {
    background: #325882;
    border: 1px solid #325882;
    color: #ffffff;
    &:hover:not(:disabled) {
      background: #274669;
    }
  }

  &.republish {
    background: #ffffff;
    border: 1px solid #325882;
    color: #325882;
    &:hover:not(:disabled) {
      background: #f0f4f8;
    }
  }

  &.unpublish {
    background: #ffffff;
    border: 1px solid #fda29b;
    color: #b42318;
    &:hover:not(:disabled) {
      background: #fef3f2;
      border-color: #f04438;
    }
  }
`;

const ConfirmScrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(44, 49, 56, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const ConfirmBox = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);

  h3 {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 600;
    color: #1d2939;
  }

  p {
    margin: 0 0 20px;
    font-size: 14px;
    line-height: 1.5;
    color: #475467;
  }

  .btn-group {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
`;
